import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, ArrowRightCircle, Mail, User, Phone, Building2, Tag, DollarSign, UserCircle, Briefcase, CheckCircle2, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../components/ConfirmModal";
import PragmaSelect from "../components/PragmaSelect";
import StatusBadge from "../components/StatusBadge";
import MovesTimeline from "../components/MovesTimeline";
import NotesTimeline from "../components/NotesTimeline";
import HistoryTimeline from "../components/HistoryTimeline";
import DetailsCard from "../components/DetailsCard";
const dispOpts = [
  ["not_contacted", "Not Contacted", "grey"],
  ["contact_attempted", "Contact Attempted", "yellow"],
  ["call_back_requested", "Call Back Requested", "teal"],
  ["not_contactable", "Not Contactable", "red"],
  ["not_interested", "Not Interested", "red"],
  ["not_qualified", "Not Qualified", "red"],
  ["qualification_in_progress", "Qualification In Progress", "purple"],
];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const [lead, setLead] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [users, setUsers] = useState([]);
  const [showConvert, setShowConvert] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState(null);

  const load = async () => {
    try {
      const [l, u] = await Promise.all([api.get(`/leads/${id}`), api.get("/users")]);
      setLead(l.data); setUsers(u.data);
    } catch (e) { toast.error("Lead not found"); navigate("/leads"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = async (data) => {
    try {
      const { data: updated } = await api.patch(`/leads/${id}`, data);
      setLead(updated);
      setHistoryKey(k => k + 1);
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      // Fetch created entities to display names in the success modal.
      const [contactRes, dealRes, companyRes] = await Promise.all([
        api.get(`/contacts/${data.contact_id}`).catch(() => ({ data: null })),
        api.get(`/deals/${data.deal_id}`).catch(() => ({ data: null })),
        data.company_id ? api.get(`/companies/${data.company_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setShowConvert(false);
      setConvertResult({
        deal_id: data.deal_id,
        contact: contactRes.data,
        deal: dealRes.data,
        company: companyRes.data,
      });
    } catch (e) { toast.error("Convert failed"); }
    setConverting(false);
  };

  const remove = async () => {
    if (!await confirm({ title: "Delete this lead?", message: "This cannot be undone.", destructive: true, confirmLabel: "Delete lead" })) return;
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    navigate("/leads");
  };

  if (!lead) return <div className="px-8 py-12 text-mute mono text-xs">loading…</div>;

  const disp = dispOpts.find(([k]) => k === lead.disposition) || dispOpts[0];

  return (
    <div data-testid="lead-detail-page">
      <div className="px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/leads")} className="pg-btn pg-btn-ghost" data-testid="back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 className="text-2xl" style={{ fontWeight: 600 }}>{lead.name}</h1>
            <div className="mono text-[11px] text-mute mt-1">{lead.company || "no company"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lead.converted ? (
            <StatusBadge color="teal" label="Converted" />
          ) : (
            <button onClick={() => setShowConvert(true)} className="pg-btn pg-btn-primary" data-testid="convert-lead-btn">
              <ArrowRightCircle size={14} /> Convert Lead
            </button>
          )}
          {canDelete && (
            <button onClick={remove} className="pg-btn pg-btn-danger" data-testid="delete-lead-btn">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 p-8">
        <div className="col-span-2 space-y-6">
          <div className="pg-card p-6">
            <h3 className="text-sm mono uppercase text-mute tracking-wider mb-4">Disposition</h3>
            <PragmaSelect
              value={lead.disposition}
              onChange={(v) => { setLead({ ...lead, disposition: v }); patch({ disposition: v }); }}
              options={dispOpts.map(([k, l]) => ({ value: k, label: l }))}
              testId="lead-disposition-select"
              className="mb-3"
            />
            <StatusBadge color={disp[2]} label={disp[1]} />
          </div>

          <NotesTimeline linkedType="lead" linkedId={lead.id} />
          <MovesTimeline linkedType="lead" linkedId={lead.id} users={users} contactEmail={lead.email} contactPhone={lead.phone} />
          <HistoryTimeline key={historyKey} entityType="lead" entityId={lead.id} />
        </div>

        <div className="space-y-6">
          <DetailsCard
            testId="lead-details-card"
            createdAt={lead.created_at}
            updatedAt={lead.updated_at}
            onSave={patch}
            fields={[
              { key: "name",    label: "Name",    value: lead.name,    icon: User },
              { key: "email",   label: "Email",   value: lead.email,   icon: Mail,      type: "email" },
              { key: "phone",   label: "Phone",   value: lead.phone,   icon: Phone,     type: "tel" },
              { key: "company", label: "Company", value: lead.company, icon: Building2 },
              { key: "role",    label: "Role",    value: lead.role,    icon: Briefcase },
              { key: "source",  label: "Source",  value: lead.source,  icon: Tag },
              { key: "value",   label: "Value",   value: lead.value,   icon: DollarSign, type: "number",
                display: (v) => v != null ? `$${Number(v).toLocaleString()}` : "—" },
              { key: "owner_id", label: "Owner", value: lead.owner_id, icon: UserCircle, type: "select",
                options: users.map(u => ({ value: u.id, label: u.name })),
                display: () => (users.find(u => u.id === lead.owner_id)?.name || "—") },
            ]}
          />
        </div>
      </div>

      {showConvert && (
        <ConvertConfirmModal
          leadName={lead.name}
          onCancel={() => setShowConvert(false)}
          onConfirm={convert}
          busy={converting}
        />
      )}
      {convertResult && (
        <ConvertSuccessModal
          result={convertResult}
          onClose={() => { setConvertResult(null); load(); }}
          onViewDeal={() => navigate(`/deals/${convertResult.deal_id}`)}
        />
      )}
    </div>
  );
}


function ConvertSuccessModal({ result, onClose, onViewDeal }) {
  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} data-testid="convert-success-backdrop" />
      <div
        className="pg-card"
        role="dialog" aria-modal="true"
        data-testid="convert-success-modal"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          padding: 0, width: "min(480px, 92vw)", zIndex: 200,
          borderRadius: 12, overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.20)",
        }}
      >
        <div style={{ padding: "22px 24px 8px", display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{
            width: 40, height: 40, flexShrink: 0, borderRadius: "50%",
            background: "rgba(16,185,129,0.12)", color: "#059669",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <CheckCircle2 size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Lead converted</h3>
            <p style={{ fontSize: 13, color: "var(--pg-text-mute)", margin: "6px 0 0", lineHeight: 1.55 }}>
              We created the following records from this lead:
            </p>
          </div>
        </div>
        <div style={{ padding: "8px 24px 4px" }}>
          <ConvertResultRow icon={User}     label="Contact" name={result.contact?.name} testId="convert-result-contact" />
          {result.company && (
            <ConvertResultRow icon={Building2} label="Company" name={result.company?.name} testId="convert-result-company" />
          )}
          <ConvertResultRow icon={Briefcase} label="Deal"    name={result.deal?.name}    testId="convert-result-deal" />
        </div>
        <div style={{ padding: "16px 24px 20px", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="pg-btn" onClick={onClose} data-testid="convert-success-close">Close</button>
          <button className="pg-btn pg-btn-primary" onClick={onViewDeal} data-testid="convert-success-view-deal">
            <ExternalLink size={13} /> View deal
          </button>
        </div>
      </div>
    </>
  );
}

function ConvertResultRow({ icon: Icon, label, name, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
        border: "1px solid var(--pg-border)", borderRadius: 8, marginBottom: 8,
        background: "var(--pg-surface)",
      }}
    >
      <Icon size={16} style={{ color: "var(--pg-text-mute)", flexShrink: 0 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="mono text-[10px] uppercase tracking-wider" style={{ color: "var(--pg-text-mute)" }}>{label}</div>
        <div style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {name || "—"}
        </div>
      </div>
    </div>
  );
}


function ConvertConfirmModal({ leadName, onCancel, onConfirm, busy }) {
  return (
    <>
      <div className="side-panel-backdrop" onClick={busy ? undefined : onCancel} />
      <div
        className="pg-card"
        data-testid="convert-confirm-modal"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          padding: "1.75rem 2rem",
          width: "min(440px, 90vw)",
          zIndex: 60,
        }}
      >
        <h2 className="text-xl mb-2" style={{ fontWeight: 600 }}>Are you sure you want to convert this lead?</h2>
        <p className="text-sm text-dim mb-1">
          Lead <strong style={{ color: "var(--pg-text)" }}>{leadName}</strong> will be converted.
        </p>
        <p className="mono text-[11px] text-mute mb-6">
          A new Contact, Deal and (if needed) Company will be created. This can't be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="pg-btn"
            onClick={onCancel}
            disabled={busy}
            data-testid="convert-cancel"
          >
            Oops, not yet
          </button>
          <button
            type="button"
            className="pg-btn pg-btn-primary"
            onClick={onConfirm}
            disabled={busy}
            data-testid="convert-confirm"
          >
            {busy ? "Converting…" : "Yes, convert"}
          </button>
        </div>
      </div>
    </>
  );
}
